import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useClosedDays, getCashbook, store } from "@/lib/pos-store";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/reports/closed/$date")({
  head: ({ params }) => ({
    meta: [
      { title: `Report: ${params.date}` },
      {
        name: "description",
        content: "Detailed report for a closed day.",
      },
    ],
  }),
  component: ClosedDayDetail,
});

function ClosedDayDetail() {
  const { date } = Route.useParams();
  const closedDays = useClosedDays();
  const day = closedDays.find((d) => d.date === date);

  if (!day) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
        <p className="text-sm font-medium text-muted-foreground">
          Closed day not found
        </p>
        <p className="mt-1 text-xs text-muted-foreground/70">
          No report exists for {date}.
        </p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
      </div>
    );
  }

  const d = new Date(date + "T00:00:00");
  const displayDate = d.toLocaleDateString("en-KE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const formattedDate = d.toLocaleDateString("en-KE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <div>
      <PageHeader
        title={formattedDate}
        subtitle="Closed day report"
        right={
          <Link to="/reports/closed">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
          </Link>
        }
      />

      <div className="space-y-4 px-4 pt-4">
        {/* Sales Section */}
        <Section title="Sales">
          <Row label="Total Sales" value={fmt(day.salesTotal)} bold />
          <Row label="Cash Sales" value={fmt(day.cashSales)} indent />
          <Row label="M-Pesa Sales" value={fmt(day.mpesaSales)} indent />
          <Row label="Bank Sales" value={fmt(day.bankSales)} indent />
        </Section>

        {/* Cashbook Section */}
        <Section title="Cashbook">
          <Row label="Previous Balance" value={fmt(day.previousBalance)} />
          <Row label="Debts Paid" value={fmt(day.debtsPaid)} />
          <Divider />
          <Row label="Purchases" value={fmt(day.purchases)} />
          <Row label="Expenditure" value={fmt(day.expenditure)} />
          <Row label="Debts Given" value={fmt(day.debts)} />
          <Row label="Banking" value={fmt(day.banking)} />
          <Row label="M-Pesa" value={fmt(day.mpesa)} />
          <Row label="Cash Handed Over" value={fmt(day.cashHandedOver)} />
          <Divider />
          <Row label="B.C.F" value={fmt(day.bcf)} bold accent />
        </Section>

        {/* Inventory snapshot */}
        {day.items.length > 0 && (
          <Section title="Closing Stock">
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[400px] text-[11px]">
                <thead className="bg-secondary text-secondary-foreground">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">Item</th>
                    <th className="px-2 py-2 text-right font-semibold">Open</th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Added
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">Sold</th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Close
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {day.items.map((item, i) => {
                    const itemSales = day.sales
                      .filter((s) => s.itemId === item.id)
                      .reduce((a, b) => a + b.qty, 0);
                    const totalStock = item.openStock + item.addedStock;
                    const closeStock = totalStock - itemSales;
                    return (
                      <tr key={i} className="bg-card">
                        <td className="px-2 py-2 font-medium">{item.name}</td>
                        <td className="px-2 py-2 text-right">
                          {item.openStock}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {item.addedStock}
                        </td>
                        <td className="px-2 py-2 text-right">{itemSales}</td>
                        <td className="px-2 py-2 text-right font-semibold">
                          {closeStock}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      <div className="space-y-2 text-sm">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
  accent,
  indent,
}: {
  label: string;
  value: string;
  bold?: boolean;
  accent?: boolean;
  indent?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${indent ? "pl-4" : ""}`}
    >
      <span
        className={`${bold ? "font-semibold" : ""} ${accent ? "text-primary" : "text-muted-foreground"} text-xs`}
      >
        {label}
      </span>
      <span
        className={`font-display ${bold ? "text-base font-bold" : "text-sm font-medium"} ${accent ? "text-primary" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-1 border-t border-dashed border-border" />;
}
